import { useQuery } from "@tanstack/react-query";
import { gqlReqNext } from "~utils/api/gql-req/next";
import {
	GetPartnerManagedAccountsQuery,
	GetPartnerManagedAccountsQueryVariables,
} from "~/types/__generated/gql/graphql";

const query = /* GraphQL */ `
	query GetPartnerManagedAccounts(
		$offset: Int!
		$limit: Int!
		$statusFilter: [PARTNER_MANAGED_ACCOUNT_STATUS_FILTER!]
		$planFilter: [SUBSCRIPTION_PLAN!]
		$cycleFilter: [SUBSCRIPTION_CYCLE!]
		$search: String
		$sort: PARTNER_MANAGED_ACCOUNT_SORT!
	) {
		getCurrentPartner {
			_managedAccounts(
				offset: $offset
				limit: $limit
				statusFilter: $statusFilter
				planFilter: $planFilter
				cycleFilter: $cycleFilter
				search: $search
				sort: $sort
			) {
				total
				statusCounts {
					ALL
					TRIAL
					PAID
					PAST_DUE
					CLOSED
				}
				items {
					id
					companyName
					slug
					partnerId
					createdAt
					isPastDue
					mangomapSupportAccess
					subscription {
						type
						paidConfig {
							plan
							cycle
							subscriptionID
							addons {
								admins
								editors
								maps
								storage
							}
						}
						trialExpiresAt
						_nextRenewalAt
						_currentPeriodAmount {
							amountMinorUnits
							currency
						}
					}
					_clientOwner {
						id
						name
						email
						isActivated
						cognitoId
						updatedAt
					}
					partnerActivationState
					isReverseHandoffInProgress
					_quotaUsage {
						mapsUsed
						mapsLimit
						storageBytesUsed
						storageBytesLimit
						viewersUsed
						viewersLimit
						isViewersUnlimited
						isAddOnsAllowed
						adminsUsed
						adminsLimit
						editorsUsed
						editorsLimit
					}
				}
			}
		}
	}
`;

export type PartnerManagedAccountsRawResponse =
	GetPartnerManagedAccountsQuery["getCurrentPartner"]["_managedAccounts"] & {
		statusCounts: {
			ALL: number;
			TRIAL: number;
			PAID: number;
			PAST_DUE: number;
			CLOSED: number;
		};
	};
export type RawManagedAccount =
	PartnerManagedAccountsRawResponse["items"][number];

export const getPartnerManagedAccountsQueryKey = (
	variables: GetPartnerManagedAccountsQueryVariables,
) => ["partnerManagedAccounts", variables];

const getPartnerManagedAccountsQueryFn = async (
	variables: GetPartnerManagedAccountsQueryVariables,
	signal?: AbortSignal,
): Promise<PartnerManagedAccountsRawResponse> => {
	return gqlReqNext<
		GetPartnerManagedAccountsQuery,
		GetPartnerManagedAccountsQueryVariables
	>({
		query,
		signal,
		variables,
	}).then((res) => res._managedAccounts);
};

export const useGetPartnerManagedAccounts = (
	variables: GetPartnerManagedAccountsQueryVariables,
) => {
	return useQuery({
		queryKey: getPartnerManagedAccountsQueryKey(variables),
		queryFn: ({ signal }) =>
			getPartnerManagedAccountsQueryFn(variables, signal),
	});
};
